import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";

const initialState = {
  products: [],
  addingProduct: false,
  addingProductError: null,
  fetchingProducts: false,
  fetchingProductsError: null,
  updatingStatus: false,
  updatingStatusError: null,
  updatingProduct: false,
  updatingProductError: null,
  deletingProduct: false,
  deletingProductError: null,
};

export const fetchAuthorizeProducts = createAsyncThunk(
  "authorize-products/fetchProducts",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("app_id", "6");
      
      if (filters.search) {
        params.append("search", filters.search);
      }
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.active_from) {
        params.append("active_from", filters.active_from);
      }
      if (filters.active_to) {
        params.append("active_to", filters.active_to);
      }

      const queryString = params.toString();
      const url = `/products/?${queryString}`;

      const response = await callApi(url, "GET");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Server error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch products");
    }
  }
);

export const updateProductStatus = createAsyncThunk(
  "authorize-products/updateStatus",
  async ({ productId, status }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}/status`, "PUT", {
        status: status,
      });

      if (!response.ok) {
        let errorMessage = "Server error";
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = typeof errorData.error === 'string' 
              ? errorData.error 
              : JSON.stringify(errorData.error);
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (jsonError) {
          try {
            const text = await response.text();
            if (text) {
              errorMessage = text;
            }
          } catch (textError) {
            errorMessage = response.statusText || "Server error";
          }
        }
        
        return rejectWithValue(errorMessage);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      let errorMessage = "Failed to update product status";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.error) {
        errorMessage = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAuthorizeProduct = createAsyncThunk(
  "authorize-products/deleteProduct",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}`, "DELETE");

      if (!response.ok) {
        let errorMessage = "Server error";
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = typeof errorData.error === 'string' 
              ? errorData.error 
              : JSON.stringify(errorData.error);
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (jsonError) {
          try {
            const text = await response.text();
            if (text) {
              errorMessage = text;
            }
          } catch (textError) {
            errorMessage = response.statusText || "Server error";
          }
        }
        
        return rejectWithValue(errorMessage);
      }
      
      try {
        const data = await response.json();
        return data;
      } catch {
        return { success: true };
      }
    } catch (error) {
      let errorMessage = "Failed to delete product";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.error) {
        errorMessage = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAuthorizeProduct = createAsyncThunk(
  "authorize-products/updateProduct",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}`, "PUT", {
        sku: productData.sku || "",
        description: productData.description || "",
        active_from: productData.active_from || null,
        active_to: productData.active_to || null,
        app: [6],
        comment: productData.comment || "",
        type:
          productData.type === "package" || productData.type === "product"
            ? productData.type
            : "product",
      });

      if (!response.ok) {
        let errorMessage = "Server error";
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = typeof errorData.error === 'string' 
              ? errorData.error 
              : JSON.stringify(errorData.error);
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (jsonError) {
          try {
            const text = await response.text();
            if (text) {
              errorMessage = text;
            }
          } catch (textError) {
            errorMessage = response.statusText || "Server error";
          }
        }
        
        return rejectWithValue(errorMessage);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      let errorMessage = "Failed to update product";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.error) {
        errorMessage = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const addAuthorizeProduct = createAsyncThunk(
  "authorize-products/addProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await callApi("/products/", "POST", {
        sku: productData.sku || "",
        description: productData.description || "",
        active_from: productData.active_from || null,
        active_to: productData.active_to || null,
        app: [6],
        comment: productData.comment || "",
        type:
          productData.type === "package" || productData.type === "product"
            ? productData.type
            : "product",
      });

      const responseData = await response.json().catch(() => ({}));

      // Preserve subscription cart body on any HTTP status (same as sendingEinvoices)
      if (responseData?.to_buy) {
        return {
          ...responseData,
          __subscriptionRequired: true,
          __httpStatus: response.status,
        };
      }

      if (!response.ok) {
        let errorMessage = "Server error";

        if (responseData.error) {
          errorMessage = typeof responseData.error === "string"
            ? responseData.error
            : JSON.stringify(responseData.error);
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (typeof responseData === "string") {
          errorMessage = responseData;
        } else {
          errorMessage = response.statusText || "Server error";
        }

        return rejectWithValue(errorMessage);
      }

      return responseData;
    } catch (error) {
      let errorMessage = "Failed to add product";

      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error.error) {
        errorMessage = typeof error.error === "string" ? error.error : JSON.stringify(error.error);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

const authorizeProductsSlice = createSlice({
  name: "authorizeProducts",
  initialState,
  reducers: {
    clearAddingError: (state) => {
      state.addingProductError = null;
    },
    clearFetchingError: (state) => {
      state.fetchingProductsError = null;
    },
    clearUpdatingStatusError: (state) => {
      state.updatingStatusError = null;
    },
    clearDeletingError: (state) => {
      state.deletingProductError = null;
    },
    clearUpdatingProductError: (state) => {
      state.updatingProductError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthorizeProducts.pending, (state) => {
        state.fetchingProducts = true;
        state.fetchingProductsError = null;
      })
      .addCase(fetchAuthorizeProducts.fulfilled, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = null;
        state.products = action.payload || [];
      })
      .addCase(fetchAuthorizeProducts.rejected, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = action.payload || "Failed to fetch products";
      })
      .addCase(addAuthorizeProduct.pending, (state) => {
        state.addingProduct = true;
        state.addingProductError = null;
      })
      .addCase(addAuthorizeProduct.fulfilled, (state) => {
        state.addingProduct = false;
        state.addingProductError = null;
      })
      .addCase(addAuthorizeProduct.rejected, (state, action) => {
        state.addingProduct = false;
        state.addingProductError = action.payload || "Failed to add product";
      })
      .addCase(updateAuthorizeProduct.pending, (state) => {
        state.updatingProduct = true;
        state.updatingProductError = null;
      })
      .addCase(updateAuthorizeProduct.fulfilled, (state) => {
        state.updatingProduct = false;
        state.updatingProductError = null;
      })
      .addCase(updateAuthorizeProduct.rejected, (state, action) => {
        state.updatingProduct = false;
        state.updatingProductError = action.payload || "Failed to update product";
      })
      .addCase(updateProductStatus.pending, (state) => {
        state.updatingStatus = true;
        state.updatingStatusError = null;
      })
      .addCase(updateProductStatus.fulfilled, (state) => {
        state.updatingStatus = false;
        state.updatingStatusError = null;
      })
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.updatingStatus = false;
        state.updatingStatusError = action.payload || "Failed to update product status";
      })
      .addCase(deleteAuthorizeProduct.pending, (state) => {
        state.deletingProduct = true;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizeProduct.fulfilled, (state) => {
        state.deletingProduct = false;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizeProduct.rejected, (state, action) => {
        state.deletingProduct = false;
        state.deletingProductError = action.payload || "Failed to delete product";
      });
  },
});

export const { 
  clearAddingError, 
  clearFetchingError, 
  clearUpdatingStatusError, 
  clearUpdatingProductError, 
  clearDeletingError 
} = authorizeProductsSlice.actions;
export default authorizeProductsSlice.reducer;
